import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';

export interface AiChatActionProps {
  isPremiumOnly: boolean;
}

export interface AudioLimit {
  dailyLimit: number;
  usedToday: number;
  remaining: number;
  resetsAt: string | null;
  isLimitReached: boolean;
  lastSyncedAt: string | null;
  fetchError: boolean;
}

interface AiChatState {
  isPremiumOnly: boolean | null;
  audioLimit: AudioLimit;
  isFetchingAudioLimit: boolean;
}

const initialAudioLimit: AudioLimit = {
  dailyLimit: 200,
  usedToday: 0,
  remaining: 200,
  resetsAt: null,
  isLimitReached: false,
  lastSyncedAt: null,
  fetchError: false,
};

const initialState: AiChatState = {
  isPremiumOnly: null,
  audioLimit: initialAudioLimit,
  isFetchingAudioLimit: false,
};

export const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    updateAiChatIsPremiumOnly: (state, action: PayloadAction<AiChatActionProps>) => {
      state.isPremiumOnly = action.payload.isPremiumOnly;
    },
    setAudioLimitState: (state, action: PayloadAction<Omit<AudioLimit, 'lastSyncedAt' | 'fetchError'>>) => {
      state.audioLimit = {
        ...action.payload,
        lastSyncedAt: new Date().toISOString(),
        fetchError: false,
      };
      state.isFetchingAudioLimit = false;
    },
    updateAudioLimitAfterSend: (state, action: PayloadAction<Omit<AudioLimit, 'lastSyncedAt' | 'fetchError'>>) => {
      state.audioLimit = {
        ...action.payload,
        lastSyncedAt: new Date().toISOString(),
        fetchError: false,
      };
      state.isFetchingAudioLimit = false;
    },
    setIsFetchingAudioLimit: (state, action: PayloadAction<boolean>) => {
      state.isFetchingAudioLimit = action.payload;
    },
    // Used when a fetchStatus call fails — clears the loading state without
    // overwriting isLimitReached, so a failed refetch can't re-enable a limited mic.
    setAudioLimitFetchError: (state) => {
      state.audioLimit.lastSyncedAt = new Date().toISOString();
      state.audioLimit.fetchError = true;
      state.isFetchingAudioLimit = false;
    },
  },
});

export const {updateAiChatIsPremiumOnly, setAudioLimitState, updateAudioLimitAfterSend, setIsFetchingAudioLimit, setAudioLimitFetchError} = aiChatSlice.actions;
export const aiChatReducer = aiChatSlice.reducer;
